import {
  SubTradeRequestStates,
  SubTradeRequestTypes,
} from '@generated/graphql';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { TFunction } from 'i18next';
import type {
  ReviewTradeCounts,
  ReviewTradeHoldDetail,
  ReviewTradeHoldReason,
  ReviewTradeRequest,
  ReviewTradeRow,
  ReviewTradeStatus,
} from '../../types/tradeReview.types';

export const REVIEW_TRADE_ACTIONABLE_STATES: SubTradeRequestStates[] = [
  SubTradeRequestStates.Initiated,
  SubTradeRequestStates.Ready,
];

export const isActionableReviewTrade = (
  trade: Pick<ReviewTradeRequest, 'state'>,
): boolean => REVIEW_TRADE_ACTIONABLE_STATES.includes(trade.state);

export const isVerifiedReviewTrade = (
  trade: Pick<ReviewTradeRequest, 'verifiedAt'>,
): boolean => Boolean(trade.verifiedAt);

export const resolveReviewTradeHold = (
  trade: ReviewTradeRequest,
  now: Date = new Date(),
): { reason: ReviewTradeHoldReason; until?: string } | undefined => {
  if (trade.blockedUntilAccountActive) {
    return { reason: 'accountInactive' };
  }

  if (trade.blockedUntilSubAccountActive) {
    return { reason: 'subAccountInactive' };
  }

  if (trade.blockedUntil && new Date(trade.blockedUntil) > now) {
    return { reason: 'blockedUntil', until: trade.blockedUntil };
  }

  const nextDate = trade.schedule?.nextDate;
  if (nextDate && new Date(nextDate) > now) {
    return { reason: 'schedule', until: nextDate };
  }

  return undefined;
};

export const resolveReviewTradeStatus = (
  trade: ReviewTradeRequest,
  evaluated: boolean,
  now: Date = new Date(),
): {
  status: ReviewTradeStatus;
  holdReason?: ReviewTradeHoldReason;
  holdUntil?: string;
} => {
  if (trade.state === SubTradeRequestStates.Canceled) {
    return { status: 'CANCELED' };
  }

  if (trade.state === SubTradeRequestStates.Requested) {
    return { status: 'REQUESTED' };
  }

  if (trade.state === SubTradeRequestStates.Reconciled) {
    return { status: 'RECONCILED' };
  }

  if (trade.state === SubTradeRequestStates.Ready) {
    return { status: 'READY' };
  }

  const hold = resolveReviewTradeHold(trade, now);
  if (evaluated && hold) {
    return {
      status: 'HELD',
      holdReason: hold.reason,
      holdUntil: hold.until,
    };
  }

  return { status: 'INITIATED' };
};

export const toReviewTradeRow = (
  trade: ReviewTradeRequest,
  evaluated: boolean,
  now: Date = new Date(),
): ReviewTradeRow => {
  const { status, holdReason, holdUntil } = resolveReviewTradeStatus(
    trade,
    evaluated,
    now,
  );

  return {
    ...trade,
    status,
    holdReason,
    holdUntil,
    isActionable: isActionableReviewTrade(trade),
    isHeld: status === 'HELD',
    isVerified: isVerifiedReviewTrade(trade),
  };
};

export const resolveReviewTradeExpectedPriceCents = (
  trade: Pick<
    ReviewTradeRequest,
    'expectedTradePriceCents' | 'financialProduct'
  >,
): number | undefined => {
  const priceCents =
    trade.expectedTradePriceCents ?? trade.financialProduct?.currentPriceCents;
  if (typeof priceCents === 'number' && priceCents > 0) {
    return priceCents;
  }

  return undefined;
};

export const resolveReviewTradeShares = (
  trade: Pick<
    ReviewTradeRequest,
    | 'expectedQuantity'
    | 'expectedTradePriceCents'
    | 'moneyAllocatedCents'
    | 'financialProduct'
  >,
): number | undefined => {
  if (
    typeof trade.expectedQuantity === 'number' &&
    trade.expectedQuantity > 0
  ) {
    return trade.expectedQuantity;
  }

  const priceCents =
    trade.expectedTradePriceCents ?? trade.financialProduct?.currentPriceCents;
  if (
    typeof priceCents === 'number' &&
    priceCents > 0 &&
    typeof trade.moneyAllocatedCents === 'number'
  ) {
    return Math.abs(trade.moneyAllocatedCents) / priceCents;
  }

  return undefined;
};

export const formatReviewTradeShares = (
  t: TFunction,
  trade: Pick<
    ReviewTradeRequest,
    | 'isSellAll'
    | 'expectedQuantity'
    | 'expectedTradePriceCents'
    | 'moneyAllocatedCents'
    | 'financialProduct'
  >,
): string => {
  if (trade.isSellAll) {
    return t('reviewTrades.sharesAll');
  }

  const shares = resolveReviewTradeShares(trade);
  if (shares === undefined) {
    return '—';
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(shares);
};

export const resolveTradeSettlementDays = (
  trade: Pick<ReviewTradeRequest, 'settlementDays' | 'financialProduct'>,
): number | undefined => {
  if (typeof trade.settlementDays === 'number') {
    return trade.settlementDays;
  }

  if (typeof trade.financialProduct?.settlementDays === 'number') {
    return trade.financialProduct.settlementDays;
  }

  return undefined;
};

const sleeveKey = (
  trade: Pick<ReviewTradeRequest, 'portfolioReport' | 'subAccount'>,
) =>
  `${trade.portfolioReport ?? 'unknown'}:${trade.subAccount?.id ?? 'unknown'}`;

const findLaterSellProceeds = (
  buy: ReviewTradeRow,
  siblings: ReviewTradeRow[],
): ReviewTradeHoldDetail | undefined => {
  const buyDays = resolveTradeSettlementDays(buy);
  let laterSell: ReviewTradeRow | undefined;
  let laterSellDays: number | undefined;

  for (const sibling of siblings) {
    if (sibling.id === buy.id || sibling.type !== SubTradeRequestTypes.Sell) {
      continue;
    }

    if (
      sibling.state !== SubTradeRequestStates.Initiated &&
      sibling.state !== SubTradeRequestStates.Ready
    ) {
      continue;
    }

    const sellDays = resolveTradeSettlementDays(sibling);
    if (sellDays === undefined) {
      continue;
    }

    if (buyDays !== undefined && sellDays <= buyDays) {
      continue;
    }

    if (laterSellDays === undefined || sellDays > laterSellDays) {
      laterSell = sibling;
      laterSellDays = sellDays;
    }
  }

  if (laterSellDays === undefined) {
    return buyDays !== undefined ? { buySettlementDays: buyDays } : undefined;
  }

  return {
    buySettlementDays: buyDays,
    sellSettlementDays: laterSellDays,
    sellTicker: laterSell?.financialProduct?.ticker ?? undefined,
  };
};

export const resolvePendingWithdrawCents = (
  trade: Pick<ReviewTradeRequest, 'portfolioReport' | 'subAccount'>,
  reportsById: Map<string, PortfolioReport>,
): number => {
  const report = trade.portfolioReport
    ? reportsById.get(trade.portfolioReport)
    : undefined;
  const subAccountId = trade.subAccount?.id;
  if (!report || !subAccountId) {
    return 0;
  }

  const match = report.subAccounts?.find(
    (subAccountReport) => subAccountReport.subAccount?.id === subAccountId,
  );

  return match?.pendingWithdrawCents ?? 0;
};

export const applyPendingTradeExplanations = (
  rows: ReviewTradeRow[],
  evaluated: boolean,
  reportsById: Map<string, PortfolioReport> = new Map(),
): ReviewTradeRow[] =>
  rows.map((row) => {
    if (row.holdReason || row.status !== 'INITIATED') {
      return row;
    }

    if (row.type !== SubTradeRequestTypes.Buy) {
      return row;
    }

    const siblings = rows.filter(
      (candidate) => sleeveKey(candidate) === sleeveKey(row),
    );
    const holdDetail = findLaterSellProceeds(row, siblings);
    const hasLaterSell =
      holdDetail?.sellSettlementDays !== undefined &&
      (holdDetail.buySettlementDays === undefined ||
        holdDetail.sellSettlementDays > holdDetail.buySettlementDays);

    if (hasLaterSell) {
      return {
        ...row,
        status: evaluated ? 'HELD' : row.status,
        isHeld: evaluated ? true : row.isHeld,
        holdReason: 'awaitingProceeds',
        holdDetail,
      };
    }

    if (evaluated && resolvePendingWithdrawCents(row, reportsById) > 0) {
      return {
        ...row,
        status: 'HELD',
        isHeld: true,
        holdReason: 'pendingWithdraw',
      };
    }

    if (evaluated) {
      return {
        ...row,
        status: 'HELD',
        isHeld: true,
        holdReason: 'awaitingProceeds',
        holdDetail,
      };
    }

    return row;
  });

export const formatReviewTradeWhy = (
  t: TFunction,
  row: Pick<ReviewTradeRow, 'holdReason' | 'holdUntil' | 'holdDetail'>,
  localizedDate: (value: string) => string,
): { label: string; tooltip?: string } | undefined => {
  if (!row.holdReason) {
    return undefined;
  }

  if (row.holdReason === 'pendingWithdraw') {
    return {
      label: t('reviewTrades.note.pendingWithdraw'),
      tooltip: t('reviewTrades.note.pendingWithdrawTooltip'),
    };
  }

  if (row.holdReason === 'awaitingProceeds') {
    const sellTicker = row.holdDetail?.sellTicker;

    return {
      label: t('reviewTrades.note.awaitingProceeds'),
      tooltip: sellTicker
        ? (t('reviewTrades.note.awaitingProceedsWithTickerTooltip', {
            ticker: sellTicker,
          }) ?? undefined)
        : (t('reviewTrades.note.awaitingProceedsTooltip') ?? undefined),
    };
  }

  if (
    (row.holdReason === 'schedule' || row.holdReason === 'blockedUntil') &&
    row.holdUntil
  ) {
    return {
      label: t(`reviewTrades.holdReason.${row.holdReason}`, {
        date: localizedDate(row.holdUntil),
      }),
    };
  }

  return { label: t(`reviewTrades.holdReason.${row.holdReason}`) };
};

export const hasReviewTradeNotes = (rows: ReviewTradeRow[]): boolean =>
  rows.some((row) => Boolean(row.holdReason));

export const buildReviewTradeRows = (
  trades: ReviewTradeRequest[],
  evaluated: boolean,
  now: Date = new Date(),
  reportsById: Map<string, PortfolioReport> = new Map(),
): ReviewTradeRow[] =>
  applyPendingTradeExplanations(
    trades.map((trade) => toReviewTradeRow(trade, evaluated, now)),
    evaluated,
    reportsById,
  );

export const buildReviewTradeCounts = (
  rows: ReviewTradeRow[],
): ReviewTradeCounts =>
  rows.reduce<ReviewTradeCounts>(
    (counts, row) => {
      counts.total += 1;

      if (row.isActionable) {
        counts.actionable += 1;
        if (!row.isVerified) {
          counts.unverifiedActionable += 1;
        }
      }

      if (row.status === 'READY') {
        counts.ready += 1;
      } else if (row.status === 'HELD') {
        counts.held += 1;
      } else if (row.status === 'INITIATED') {
        counts.pending += 1;
      } else if (row.status === 'CANCELED') {
        counts.canceled += 1;
      }

      return counts;
    },
    {
      total: 0,
      actionable: 0,
      ready: 0,
      held: 0,
      pending: 0,
      canceled: 0,
      unverifiedActionable: 0,
    },
  );
